import _ from "lodash";
import printJS from "print-js";
import moment from "moment";
import sanitize from "sanitize-filename";

let naturalSortCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
});

export function getGMapsAutocompletePlace(place) {
    let location = place.location;
    let addressToGo = {
        lat: Number(location.lat().toFixed(8)),
        lng: Number(location.lng().toFixed(8)),
    };
    if (place.addressComponents) {
        let addressComponents = place.addressComponents;
        let streetNumber = _.get(
            _.find(addressComponents, function (o) {
                return _.includes(o.types, "street_number");
            }),
            "shortText",
        );
        let streetName = _.get(
            _.find(addressComponents, function (o) {
                return _.includes(o.types, "route");
            }),
            "shortText",
        );
        addressToGo.street_address = streetNumber + " " + streetName;
        addressToGo.city = _.get(
            _.find(addressComponents, function (o) {
                return _.includes(o.types, "locality");
            }),
            "shortText",
        );
        addressToGo.state = _.get(
            _.find(addressComponents, function (o) {
                return _.includes(o.types, "administrative_area_level_1");
            }),
            "shortText",
        );
        addressToGo.zipcode = _.get(
            _.find(addressComponents, function (o) {
                return _.includes(o.types, "postal_code");
            }),
            "shortText",
        );
    }
    return addressToGo;
}

export function print(elementID) {
    printJS({
        printable: elementID,
        type: "html",
        targetStyles: ["*"],
        maxWidth: 2000,
        css: "https://fonts.googleapis.com/icon?family=Material+Icons",
    });
}

export function combineFilters(incomingFilters) {
    let filters = {};
    for (let filter of incomingFilters) {
        let result = filter.out;
        let name = filter.name;
        let newParam;
        if (!_.isEmpty(result)) {
            newParam = result;
        } else {
            newParam = undefined;
        }
        filters[name] = newParam;
    }
    return filters;
}

export function formatDatetime(date, time) {
    return date + " " + time;
}

export function getCloudinaryUrl(public_id) {
    return "https://res.cloudinary.com/harvust/image/upload/" + public_id;
}

export function createCloudinaryPollUrl(public_id) {
    return getCloudinaryUrl(
        "pg_9999999/v" + moment().valueOf() + "/" + public_id,
    );
}

export function createCloudinaryContextString(contextMap) {
    let parts = [];
    for (let [key, value] of Object.entries(contextMap)) {
        if (value === undefined || value === null || value === "") {
            continue;
        }
        parts.push(`${key}=${value}`);
    }
    return parts.join("|");
}

export function lazyLoad(transition, ...urls) {
    urls = _.map(urls, function (url) {
        return { type: "js", path: url };
    });
    return transition.injector().get("$ocLazyLoad").load(urls);
}

export function getContactWorkerDisplayName(conversation, growerMembership) {
    if (conversation.is_anonymous) {
        return "Anonymous";
    } else {
        let firstName = _.get(growerMembership, "first_name", "");
        let lastName = _.get(growerMembership, "last_name", "");
        return _.trim(firstName + " " + lastName);
    }
}

export function getLatestMembership(memberships, userSid) {
    let relevantGrowerMemberships = _.filter(memberships, ["user", userSid]);
    return _.last(
        _.orderBy(relevantGrowerMemberships, function (membership) {
            return moment(membership.created);
        }),
    );
}

export function mapUserWithGrowerMembershipName(user, growerMembership) {
    if (!user) {
        return user;
    }
    return {
        ...user,
        first_name: _.get(
            growerMembership,
            "first_name",
            _.get(user, "first_name"),
        ),
        last_name: _.get(
            growerMembership,
            "last_name",
            _.get(user, "last_name"),
        ),
    };
}

export function mapUsersWithGrowerMembershipNames(users, growerMemberships) {
    let membershipsByUserSid = _.keyBy(growerMemberships || [], "user");
    return _.map(users || [], function (user) {
        return mapUserWithGrowerMembershipName(
            user,
            membershipsByUserSid[user.sid],
        );
    });
}

export function underscoreSanitize(content) {
    return sanitize(content, { replacement: "_" });
}

export function getFileExtensionFromUrl(url) {
    let cleanUrl = _.first(_.split(url || "", "#"));
    cleanUrl = _.first(_.split(cleanUrl, "?"));
    let lastPathSegment = _.last(_.split(cleanUrl, "/"));
    let extensionMatch = /\.([a-zA-Z0-9]+)$/.exec(lastPathSegment || "");
    return extensionMatch ? _.toLower(extensionMatch[1]) : undefined;
}

export function getFileExtensionFromMimeType(mimeType) {
    return _.get(
        {
            "application/pdf": "pdf",
            "image/png": "png",
            "image/jpeg": "jpg",
            "image/jpg": "jpg",
            "image/gif": "gif",
            "image/webp": "webp",
        },
        _.toLower(mimeType || ""),
    );
}

export function addFileExtension(filename, extension) {
    if (extension) {
        return filename + "." + extension;
    }
    return filename;
}

export function buildBadgeDownloadFilename(
    growerMembership,
    downloadUrl,
    mimeType,
) {
    let filename = underscoreSanitize(
        _.get(growerMembership, "first_name", "") +
            "_" +
            _.get(growerMembership, "last_name", "") +
            "_" +
            _.get(growerMembership, "employee_id", "") +
            "_badge",
    );
    let extension = getFileExtensionFromUrl(downloadUrl);
    if (!extension) {
        extension = getFileExtensionFromMimeType(mimeType);
    }
    return addFileExtension(filename, extension);
}

export function downloadFile(filePath, newTab = false) {
    var link = document.createElement("a");
    link.href = filePath;
    link.rel = "noopener";
    if (newTab) {
        link.target = "_blank";
    }
    link.click();
}

export function prepend(value, array) {
    var newArray = array.slice();
    newArray.unshift(value);
    return newArray;
}

export function urlParamFilter(collection, availableFilters, params) {
    return _.filter(collection, function (element) {
        let filterResponses = _.map(
            availableFilters,
            function (filterConfig, name) {
                let filterInURL = params[name];
                let flag;
                if (filterInURL) {
                    flag = filterConfig.func(element, filterInURL);
                } else {
                    flag = true;
                }
                return flag;
            },
        );
        return _.every(filterResponses);
    });
}

export function urlParamSort(collection, sorts, orderingKey) {
    let ordering = sorts[orderingKey];
    return _.orderBy(collection, ordering.func, ordering.order);
}

export function sortNatural(array, key, order = "asc") {
    return _.clone(array || []).sort(function (firstElement, secondElement) {
        let firstValue =
            key === undefined ? firstElement : _.get(firstElement, key);
        let secondValue =
            key === undefined ? secondElement : _.get(secondElement, key);

        firstValue = firstValue == null ? "" : String(firstValue);
        secondValue = secondValue == null ? "" : String(secondValue);

        let comparison = naturalSortCollator.compare(firstValue, secondValue);
        return order === "desc" ? -comparison : comparison;
    });
}
